/* Typed HTTP client.
 *
 * Request/response shapes come from @ascent/shared, so a server contract change
 * that the client has not adopted is a COMPILE error rather than a runtime
 * surprise (ADR-08, risk R9).
 *
 * The device token is the only credential the client ever holds. It grants
 * learning-data access and nothing else: no admin surface, no secrets (T1).
 */

import type { EnrollResponse, HealthResponse, MeResponse } from '@ascent/shared';

// Re-exported so screens import their contract types from one place.
export type { MeResponse, HealthResponse, EnrollResponse };

const TOKEN_KEY = 'ascent.device_token';
const DEVICE_KEY = 'ascent.device_id';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string, deviceId: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(DEVICE_KEY, deviceId);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(DEVICE_KEY);
}

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body) headers.set('content-type', 'application/json');

  const token = getToken();
  if (token) headers.set('authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(path, { ...init, headers });
  } catch {
    // Distinguish "no connection" from "server said no" — the UI copy differs.
    throw new ApiFailure(0, 'OFFLINE', 'No connection.');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* fall through to the generic error below */
  }

  if (!res.ok) {
    const body = parsed as ErrorBody | null;
    throw new ApiFailure(
      res.status,
      body?.error?.code ?? 'UNKNOWN',
      // The server guarantees error.message is safe to display (05 §1.4).
      body?.error?.message ?? `Request failed (${res.status}).`,
    );
  }

  return parsed as T;
}

/* Atlas response. Shapes mirror 05 §3.1; the client is a tolerant reader, so
 * unknown fields the server adds later are simply ignored (§1.1). */
export interface AtlasTopicDto {
  topic_id: string;
  title: string;
  domain: string;
  secondary_domain: string | null;
  regime: 'A' | 'B';
  weight: number;
  bonus_weight: number;
  prereqs: string[];
  retired: boolean;
  units: {
    unit_id: string;
    unit_type: 'lesson' | 'drill_deck' | 'quiz' | 'mission' | 'lab' | 'build';
    title: string;
    version: number;
    content_hash: string;
    est_minutes: number;
    /** CR-020 ledger ids, present on Units authored since the topic ledger. */
    covers?: string[];
  }[];
}

export interface AtlasResponse {
  release_id: string;
  clusters: { cluster_id: string; name: string; domain_ids: string[] }[];
  domains: { domain_id: string; name: string; cluster_id: string }[];
  topics: AtlasTopicDto[];
  /* CR-021: the competency ledger's denominators, emitted by the Factory into
   * atlas.json. Optional — a release built before this simply reports none. */
  topic_ledger?: { total: number; by_domain?: Record<string, number> };
  expeditions: {
    id: string;
    peak: string;
    status: string;
    goal_md: string;
    topic_ids: string[];
    camps: { id: string; name: string; altitude_pct: number; assessment_unit_id: string }[];
    summit_assessment_unit_id: string;
  }[];
  active_expedition_id: string | null;
  mastery: { topic_id: string; level: number; eroding: boolean }[];
  retired_topic_ids: string[];
}

export interface ProvenanceSourceDto {
  kind: 'course' | 'doc' | 'generated';
  ref: string;
  note?: string;
}

export interface UnitDto {
  id: string;
  type: 'lesson' | 'drill_deck' | 'quiz' | 'mission' | 'lab' | 'build';
  topic_id: string;
  title: string;
  version: number;
  est_minutes: number;
  body_md: string;
  provenance: { regime: 'A' | 'B'; sources: ProvenanceSourceDto[]; verify_by?: string };
  sections_md?: { id: string; heading: string; md: string }[];
  /** drill_deck only — the deck's Cards. Scheduling lives on the server. */
  cards?: { id: string; front_md: string; back_md: string; hint: string | null }[];
  /* build only (CR-020). `solution_code` is deliberately absent: the server
   * strips it from every response, so it is not part of this contract. */
  goal_md?: string;
  starter_code?: string;
  checks_py?: string;
  seed_sql?: string;
  reference_note_md?: string;
  proof?: boolean;
  prediction?: {
    stem_md: string;
    options: {
      id: string;
      label_md: string;
      asserts: { outcome: 'all_pass' | 'any_fail'; check?: string };
    }[];
  };
  covers?: { topic_items: string[]; sources: string[] };
}

export interface QueueEntry {
  card_id: string;
  unit_id: string;
  kind: 'due' | 'new';
}

export interface TodayPlan {
  day: string;
  plan_version: string;
  generated_at: string;
  active_release_id: string | null;
  expedition: {
    expedition_id: string;
    peak_name: string;
    altitude_m: number;
    next_camp: { camp_id: string; name: string; altitude_pct: number; status: string } | null;
  } | null;
  drills: {
    due_count: number;
    new_count: number;
    new_card_cap: number;
    queue: QueueEntry[];
    projected_next_day_queue: QueueEntry[];
  };
  next_step: { kind: string; unit_id: string; title: string; est_minutes: number } | null;
  calibration_pulse: { due: boolean; items: unknown[] };
  streak: { current_days: number; week_active_days: number; week_goal: number };
  eroding_topics: { topic_id: string; title: string }[];
  units_to_prefetch: string[];
}

export interface ReviewsResponse {
  results: { event_id: string; status: 'accepted' | 'duplicate' | 'rejected'; reason?: string }[];
  due_summary: {
    due_now: number; due_rest_of_day: number;
    new_remaining_today: number; next_due_at: string | null;
  };
  sync: {
    active_release_id: string | null;
    state_epoch: string;
    plan_version: string;
    state_digest: Record<string, number>;
  };
}

export interface ProgressResponse {
  expedition: {
    expedition_id: string; peak_name: string; status: string;
    started_at: string | null; planned_weeks: number | null;
    altitude_m: number; earned_points: number; total_points: number;
  } | null;
  camps: {
    camp_id: string; name: string; altitude_pct: number; altitude_m: number;
    status: 'established' | 'in_reach' | 'ahead'; passed_at: string | null;
  }[];
  calibration: {
    window_days: number;
    mean_brier: number | null;
    curve: { confidence: number; accuracy: number | null; n: number }[];
    oci: { value_pp: number; direction: string };
    domains: { domain_id: string; gap_pp: number; mean_brier: number; n: number; flagged: boolean }[];
    flag_rule: { gap_pp: number; min_sample: number };
  };
  domain_heat: {
    domain_id: string; cluster_id: string; topic_count: number;
    mean_level: number; assessed_count: number; applied_count: number; eroding_count: number;
  }[];
  eroding_topics: {
    topic_id: string; title: string; eroding_since: string | null;
    mean_retrievability: number | null;
  }[];
  streak: { current_days: number; week_active_days: number; week_goal: number };
  mastery_totals: { by_level: number[] };
}

export interface QuizAttemptResult {
  attempt_id: string;
  status: 'accepted' | 'duplicate';
  score: number;
  passed: boolean;
  mastery_changes: { topic_id: string; previous_level: number; level: number }[];
  altitude: { altitude_m: number; delta_m: number };
  camp_established?: { camp_id: string; name: string };
}

export const api = {
  health: () => request<HealthResponse>('/api/health'),
  progress: () => request<ProgressResponse>('/api/progress'),
  postQuizAttempt: (body: unknown) =>
    request<QuizAttemptResult>('/api/quiz-attempts', {
      method: 'POST', body: JSON.stringify(body),
    }),
  atlas: () => request<AtlasResponse>('/api/atlas'),
  unit: (unitId: string) => request<UnitDto>(`/api/units/${encodeURIComponent(unitId)}`),
  today: () => request<TodayPlan>('/api/session/today'),
  postReviews: (events: unknown[]) =>
    request<ReviewsResponse>('/api/reviews', {
      method: 'POST',
      body: JSON.stringify({ events }),
    }),

  enroll: (enrollmentCode: string, deviceName: string) =>
    request<EnrollResponse>('/api/auth/enroll', {
      method: 'POST',
      body: JSON.stringify({ enrollment_code: enrollmentCode, device_name: deviceName }),
    }),

  me: () => request<MeResponse>('/api/me'),

  patchSettings: (patch: Record<string, unknown>) =>
    request<MeResponse['settings']>('/api/settings', {
      method: 'PATCH', body: JSON.stringify(patch),
    }),

  rotateToken: () =>
    request<{ device_token: string }>('/api/auth/rotate', { method: 'POST', body: JSON.stringify({}) }),

  missions: () => request<{ items: MissionDto[] }>('/api/missions'),
  missionProgress: (missionUnitId: string, body: Record<string, unknown>) =>
    request<{ status: string }>(`/api/missions/${encodeURIComponent(missionUnitId)}/progress`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  submitLab: (labUnitId: string, body: Record<string, unknown>) =>
    request<{ submission_id: string; status: string }>(`/api/labs/${encodeURIComponent(labUnitId)}/submissions`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  pushPublicKey: () => request<{ public_key: string }>('/api/push/public-key'),
  registerPush: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ subscription_id: string }>('/api/push/subscriptions', {
      method: 'POST', body: JSON.stringify(sub),
    }),
  deletePush: () => request<void>('/api/push/subscriptions', { method: 'DELETE' }),

  tutorThreads: () => request<TutorThreadList>('/api/tutor/threads'),
  tutorThread: (id: string) => request<TutorThreadDetail>(`/api/tutor/threads/${encodeURIComponent(id)}`),
  createTutorThread: (body: { mode: TutorMode; unit_id?: string | null }) =>
    request<TutorThreadSummary>('/api/tutor/threads', { method: 'POST', body: JSON.stringify(body) }),
};

export interface MissionStepDto {
  step_id: string;
  position: number;
  kind: 'investigate' | 'build' | 'read' | 'submit';
  lab_unit_id: string | null;
  status: 'locked' | 'available' | 'in_progress' | 'submitted' | 'done';
  blocked_by: string[];
  deliverable: unknown;
  title?: string;
  instructions_md?: string;
}

export interface MissionDto {
  mission_unit_id: string;
  title: string;
  brief_md: string;
  status: 'not_started' | 'active' | 'completed';
  steps: MissionStepDto[];
}

export type TutorMode = 'explain' | 'quiz_me' | 'socratic' | 'review';

export interface BudgetBlock {
  month: string;
  spent_eur: number;
  cap_eur: number;
  pct: number;
}

export interface TutorThreadSummary {
  thread_id: string;
  title: string;
  mode: TutorMode;
  unit_id: string | null;
  created_at: string;
  message_count?: number;
  last_message_at?: string | null;
}

export interface TutorThreadList {
  items: TutorThreadSummary[];
  budget: BudgetBlock;
}

export interface TutorMessage {
  message_id: string;
  role: 'user' | 'assistant' | 'system';
  content_md: string;
  created_at: string;
}

export interface TutorThreadDetail extends TutorThreadSummary {
  messages: TutorMessage[];
  budget: BudgetBlock;
}

export interface TutorStreamHandlers {
  onStart(info: { assistant_message_id: string; replayed: boolean }): void;
  onDelta(text: string): void;
  onDone(info: { month_spent_eur?: number; month_cap_eur?: number }): void;
  onError(code: string, message: string): void;
}

/**
 * POST a Tutor message and consume the SSE reply.
 *
 * `EventSource` cannot POST and cannot send an Authorization header, so this
 * reads the body stream directly. Events are separated by a blank line; a
 * partial tail is kept in the buffer until the rest of it arrives, because a
 * chunk boundary lands mid-event often enough to matter.
 */
export async function streamTutorMessage(
  threadId: string,
  messageId: string,
  content: string,
  handlers: TutorStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const headers = new Headers({ 'content-type': 'application/json', accept: 'text/event-stream' });
  const token = getToken();
  if (token) headers.set('authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(`/api/tutor/threads/${encodeURIComponent(threadId)}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message_id: messageId, content }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    handlers.onError('OFFLINE', 'No connection — the Tutor needs the server.');
    return;
  }

  if (!res.ok || !res.body) {
    // Errors before streaming starts arrive as JSON, not SSE (05 §5.4).
    let code = 'ERROR';
    let message = 'The Tutor could not answer.';
    try {
      const body = (await res.json()) as ErrorBody & { budget?: BudgetBlock };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch { /* keep the defaults */ }
    handlers.onError(code, message);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let split = buffer.indexOf('\n\n');
    while (split !== -1) {
      const raw = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      split = buffer.indexOf('\n\n');

      const eventLine = /^event: (.+)$/m.exec(raw);
      const dataLine = /^data: (.+)$/m.exec(raw);
      if (!eventLine || !dataLine) continue; // `: ping` keep-alives land here

      let data: Record<string, unknown>;
      try {
        data = JSON.parse(dataLine[1]!) as Record<string, unknown>;
      } catch {
        continue;
      }

      switch (eventLine[1]) {
        case 'start':
          handlers.onStart({
            assistant_message_id: String(data['assistant_message_id'] ?? ''),
            replayed: data['replayed'] === true,
          });
          break;
        case 'delta':
          handlers.onDelta(String(data['text'] ?? ''));
          break;
        case 'done':
          handlers.onDone(data as { month_spent_eur?: number; month_cap_eur?: number });
          break;
        case 'error':
          handlers.onError(String(data['code'] ?? 'ERROR'), String(data['message'] ?? 'The Tutor stopped.'));
          break;
        default:
          break;
      }
    }
  }
}
